class LightningDealBars extends HTMLElement {
	static SECONDS_PER_MINUTE = 60;
	static MINUTES_PER_HOUR = 60;
	static HOURS_PER_DAY = 24;
	static ONE_SECOND_MS = 1000;
	static ONE_MINUTE_MS = LightningDealBars.ONE_SECOND_MS * LightningDealBars.SECONDS_PER_MINUTE;
	static ONE_HOUR_MS = LightningDealBars.ONE_MINUTE_MS * LightningDealBars.MINUTES_PER_HOUR;
	static ONE_DAY_MS = LightningDealBars.ONE_HOUR_MS * LightningDealBars.HOURS_PER_DAY;
	static SELECTORS = {
		LIGHTNING_DEAL_CONTAINER: '.lightning-deal__container',
		PROGRESS_BAR: '.lightning-deal__progress-bar',
		TIMER: '.lightning-deal__timer .timer',
	};
	constructor() {
		super();
		this.cache = {};
	}
	connectedCallback() {
		this.initializeElements();
		this.setupTimer();
	}
	initializeElements() {
		this.elements = {
			progressBar: this.querySelector(LightningDealBars.SELECTORS.PROGRESS_BAR),
			timer: this.querySelector(LightningDealBars.SELECTORS.TIMER),
		};
	}
	setupTimer() {
		if (!this.elements?.timer || !this.elements?.progressBar) {
			return;
		}
		this.updateTimeRemaining();
		this.animationFrameLoop();
	}
	animationFrameLoop() {
		const timeRemaining = this.getTimeRemaining();
		if (timeRemaining <= 0) {
			return;
		}
		this.updateTimeRemaining();
		requestAnimationFrame(this.animationFrameLoop.bind(this));
	}
	updateTimeRemaining() {
		const timeRemaining = this.getTimeRemaining();
		const timerElement = this.elements.timer;
		timerElement.textContent = this.formatTime(timeRemaining);
	}
	getTimeRemaining() {
		const endEpoch = this.getEndDateEpochFromTimer();
		const endDate = new Date(endEpoch);
		const now = new Date();
		return Math.max(endDate - now, 0);
	}
	getEndDateEpochFromTimer() {
		const timerElement = this.elements.timer;
		const epochString = timerElement?.dataset?.endDate ?? '0';
		const epoch = parseInt(epochString);
		if (isNaN(epoch)) {
			return 0;
		}
		return epoch;
	}
	formatTime(time) {
		if (time <= 0) {
			return '00:00:00';
		}
		const { days, hours, minutes, seconds } = this.calculateTimeUnits(time);
		const timeString = `${hours}:${minutes}:${seconds}`;
		if (days > 0) {
			return `${days}:${timeString}`;
		}
		return timeString;
	}
	calculateTimeUnits(time) {
		const days = Math.floor(time / LightningDealBars.ONE_DAY_MS)
			.toString()
			.padStart(2, '0');
		const hours = Math.floor((time % LightningDealBars.ONE_DAY_MS) / LightningDealBars.ONE_HOUR_MS)
			.toString()
			.padStart(2, '0');
		const minutes = Math.floor((time % LightningDealBars.ONE_HOUR_MS) / LightningDealBars.ONE_MINUTE_MS)
			.toString()
			.padStart(2, '0');
		const seconds = Math.floor((time % LightningDealBars.ONE_MINUTE_MS) / LightningDealBars.ONE_SECOND_MS)
			.toString()
			.padStart(2, '0');
		return { days, hours, minutes, seconds };
	}
}
customElements.define('lightning-deal-bars', LightningDealBars);
class LightningDealBarsPDP extends LightningDealBars {
	constructor() {
		super();
		this.observer = null;
		document.addEventListener('variant_change', this.handleVariantChange.bind(this));
	}
	connectedCallback() {
		super.connectedCallback();
		this.setupMutationObserver();
	}
	disconnectedCallback() {
		super.disconnectedCallback();
		document.removeEventListener('variant_change', this.handleVariantChange.bind(this));
		this.observer.disconnect();
	}
	setupMutationObserver() {
		this.observer = new MutationObserver(this.reinitializeOnProductFormMutated.bind(this));
		this.observer.observe(this, { childList: true });
	}
	reinitializeOnProductFormMutated(mutationsList) {
		const lightningDealClass = LightningDealBars.SELECTORS.LIGHTNING_DEAL_CONTAINER.replace('.', '');
		const changedElementIsProductForm = (mutation) => mutation.target.className === lightningDealClass;
		const productFormChanged = mutationsList?.some(changedElementIsProductForm);
		if (productFormChanged) {
			this.initializeElements();
			this.setupTimer();
		}
	}
	handleVariantChange(event) {
		const selectors = [LightningDealBars.SELECTORS.LIGHTNING_DEAL_CONTAINER];
		const variantId = event.detail.id;
		this.rerender(variantId, selectors);
	}
	rerender(variantId, selectors) {
		const sectionId = this.dataset.sectionId ?? '';
		const url = this.dataset.url ? `${this.dataset.url}?section_id=${sectionId}&variant=${variantId}` : '';
		this.rerenderFromFetch(url, selectors);
	}
	rerenderFromFetch(url, selectors) {
		fetch(url)
			.then((response) => response.text())
			.then((html) => {
				this.cache[url] = html;
				this.rerenderComponents(html, selectors);
			});
	}
	rerenderComponents(html, selectors) {
		const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
		for (const selector of selectors) {
			const element = document.querySelector(selector);
			const target = parsedHTML.querySelector(selector);
			if (element && target) {
				element.innerHTML = target.innerHTML;
			}
		}
	}
}
customElements.define('lightning-deal-bars-pdp', LightningDealBarsPDP);
